import { AnyMerchant, MerchantStatusResponse } from 'APIs/merchants/merchants.types';
import { Content, H2, Row } from 'Constants/styles';
import { useMemo } from 'react';

import { BlueBtn } from '../../styles';
import { H5, MerchantStatus, MerchantStatusActions, MerchantStatusTable } from './styles';

type Props = {
  merchantId: string;
  merchant: AnyMerchant | null;
  merchantStatus: MerchantStatusResponse | null;
  isVerifyButtonLoading: boolean;
  isManKYBButtonLoading: boolean;
  handleVerify: (e: React.MouseEvent<HTMLButtonElement>) => Promise<void>;
  handleManualKYB: (e: React.MouseEvent<HTMLButtonElement>) => Promise<void>;
};

const OnboardingStatusView = ({
  merchantId,
  merchant,
  merchantStatus,
  isVerifyButtonLoading,
  isManKYBButtonLoading,
  handleVerify,
  handleManualKYB,
}: Props) => {
  const verifStatusData = useMemo(() => {
    let data = null;
    if (merchant && 'verificationResult' in merchant && merchant.verificationResult) {
      data = {
        status: merchant.status,
        verified: merchant.verificationResult.status,
        bankAccoountProvided: !merchant.verificationResult.errorCodes.includes('MissingBankAccount'),
        businessProfileCompleted: !!merchant.businessProfile.address.streetName,
        beneficialOwnersProvided: !(
          merchant.verificationResult.errorCodes.includes('PersonInformationMissing') ||
          merchant.verificationResult.errorCodes.includes('MissingGatewayPersonAccount')
        ),
        kybCompleted: merchant.verificationResult.status === 'Verified',
        verifErrors: merchant.verificationResult?.errorCodes.map(
          (code, index) => `${code}: ${merchant.verificationResult?.details[index]}`,
        ),
      };
    }
    return data;
  }, [merchant]);

  return (
    <Content>
      {verifStatusData && (
        <>
          <Row>
            <H2 className="mb-0">Onboarding status</H2>
          </Row>
          <Row>
            <H5>Merchant Id: #{merchantId}</H5>
          </Row>
          <MerchantStatus>
            <MerchantStatusTable>
              <li>
                <div className={`item-label`}>Status</div>
                <div className={`item-value`}>{verifStatusData.status}</div>
              </li>
              <li>
                <div className={`item-label`}>Verified</div>
                <div className={`item-value`}>
                  <div>{verifStatusData.verified}</div>
                </div>
              </li>
              <li>
                <div className={`item-label`}>Bank accounts provided</div>
                <div className={`item-value`}>
                  <div>{verifStatusData.bankAccoountProvided ? 'Yes' : 'No'}</div>
                </div>
              </li>
              <li>
                <div className={`item-label`}>Business profile completed</div>
                <div className={`item-value`}>
                  <div>{verifStatusData.businessProfileCompleted ? 'Yes' : 'No'}</div>
                </div>
              </li>
              <li>
                <div className={`item-label`}>Beneficial owners provided</div>
                <div className={`item-value`}>
                  <div>{verifStatusData.beneficialOwnersProvided ? 'Yes' : 'No'}</div>
                </div>
              </li>
              <li>
                <div className={`item-label`}>KYB completed</div>
                <div className={`item-value`}>
                  <div>{verifStatusData.kybCompleted ? 'Yes' : 'No'}</div>
                </div>
              </li>
              {verifStatusData.verifErrors && verifStatusData.verifErrors.length > 0 && (
                <li>
                  <div className={`item-label`}>
                    <div>Verification errors</div>
                    <ul className="error-list">
                      {verifStatusData.verifErrors.map((error) => (
                        <li className="error">{error}</li>
                      ))}
                    </ul>
                  </div>
                </li>
              )}
            </MerchantStatusTable>
            <MerchantStatusActions>
              {!verifStatusData.kybCompleted && (
                <>
                  <li>
                    <div className={`item-value`}>
                      <BlueBtn
                        onClick={handleVerify}
                        disabled={isVerifyButtonLoading}
                        title='Triggers verification process. It does not guarantee the merchant will be verified. Please hover over the "Verified" status for more information.'
                      >
                        Trigger Verification
                      </BlueBtn>
                    </div>
                  </li>
                  <li>
                    <div className={`item-value`}>
                      <BlueBtn
                        onClick={handleManualKYB}
                        disabled={isManKYBButtonLoading}
                        title="Mark KYB as completed and send onboarding complete email"
                      >
                        Manual KYB
                      </BlueBtn>
                    </div>
                  </li>
                </>
              )}
            </MerchantStatusActions>
          </MerchantStatus>
        </>
      )}
    </Content>
  );
};

export default OnboardingStatusView;
